// Shared Data Lab UI primitives — used by all /lab/* detail pages.
// Kept dependency-light so each page can compose its own layout.
import { Link } from "react-router-dom";
import { useMemo as _useMemo, useState as _useState, useEffect as _useEffect, useRef as _useRef } from "react";
import { ArrowLeft, ChevronRight, ChevronDown } from "lucide-react";

const React = { useMemo: _useMemo, useState: _useState, useEffect: _useEffect, useRef: _useRef };

export const money = (n, cur = "£") => {
  if (n === null || n === undefined || n === "") return "—";
  if (typeof n === "object") return "—";
  const symbol = typeof cur === "string" ? cur : "£"; // guard: table fmt passes (value, row)
  const v = Number(n);
  if (Number.isNaN(v)) return String(n);
  return `${symbol}${v.toFixed(2)}`;
};

export const asText = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return "—"; // don't render "[object Object]"
  return String(v);
};

export const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "—");

export const dateGB = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleString("en-GB"); } catch { return String(v); }
};

export function get(obj, path) {
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}

export function Panel({ icon: Icon, title, right, children, testid, className = "" }) {
  return (
    <section
      className={`relative rounded-xl border border-white/[0.11] bg-[#111C34] overflow-hidden ${className}`}
      style={{
        boxShadow:
          "0 18px 40px -8px rgba(0,0,0,0.55), 0 4px 10px rgba(0,0,0,0.32), 0 0 0 1px rgba(90,130,220,0.04), inset 0 1px 0 rgba(255,255,255,0.10), inset 0 -1px 0 rgba(0,0,0,0.30)",
      }}
      data-testid={testid}
    >
      {/* fake top-light */}
      <div aria-hidden className="absolute inset-0 pointer-events-none"
           style={{ background: "radial-gradient(120% 60% at 30% -10%, rgba(255,255,255,0.055) 0%, transparent 55%)" }} />
      <header
        className="relative flex items-center justify-between px-4 py-2.5 border-b border-white/[0.10] z-[1]"
        style={{
          background: "linear-gradient(180deg, rgba(255,255,255,0.055) 0%, rgba(0,0,0,0.20) 100%)",
          boxShadow: "inset 0 -1px 0 rgba(0,0,0,0.20)",
        }}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#C8A46B]" />}
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-[#C8A46B] font-mono">
            {title}
          </h3>
        </div>
        {right && <div className="text-[11px] font-mono text-slate-500">{right}</div>}
      </header>
      <div className="relative p-4 z-[1]">{children}</div>
    </section>
  );
}

export function KV({ label, value, mono = false, wide = false }) {
  return (
    <div className={wide ? "col-span-full" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className={`text-sm mt-0.5 text-slate-100 ${mono ? "font-mono" : ""} break-words`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

export function Chip({ children, tone = "slate" }) {
  const map = {
    slate:   "bg-white/5 text-slate-200 border-white/10",
    orange:  "bg-orange-500/10 text-orange-300 border-orange-500/30",
    green:   "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    red:     "bg-red-500/10 text-red-300 border-red-500/30",
    blue:    "bg-blue-500/10 text-blue-300 border-blue-500/30",
    violet:  "bg-violet-500/10 text-violet-300 border-violet-500/30",
    warn:    "bg-amber-500/10 text-amber-300 border-amber-500/30",
    teal:    "bg-teal-500/10 text-teal-300 border-teal-500/30",
    pink:    "bg-pink-500/10 text-pink-300 border-pink-500/30",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border font-mono ${map[tone] || map.slate}`}>
      {children}
    </span>
  );
}

export function BackLink({ to, label }) {
  return (
    <Link to={to} className="text-slate-400 hover:text-white inline-flex items-center gap-1 text-sm">
      <ArrowLeft className="w-4 h-4" /> {label}
    </Link>
  );
}

// Renders a nested object as a KV grid; expandable when very large.
export function ObjectGrid({ obj, cols = 2 }) {
  if (!obj || typeof obj !== "object") return <div className="text-sm text-slate-500">—</div>;
  const entries = Object.entries(obj).filter(([, v]) => !(v === null || v === undefined || v === ""));
  if (entries.length === 0) return <div className="text-sm text-slate-500">Empty.</div>;
  const colClass = cols === 3 ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1 sm:grid-cols-2";
  return (
    <div className={`grid ${colClass} gap-x-6 gap-y-3`}>
      {entries.map(([k, v]) => {
        const isScalar = !(typeof v === "object");
        return (
          <div key={k} className="flex flex-col">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{k}</div>
            {isScalar ? (
              <div className="text-sm mt-0.5 text-slate-100 break-words">{String(v)}</div>
            ) : (
              <details className="mt-0.5">
                <summary className="text-xs text-[#C8A46B] cursor-pointer">
                  {Array.isArray(v) ? `list[${v.length}]` : `object · ${Object.keys(v).length} keys`}
                </summary>
                <pre className="mt-2 text-[11px] font-mono text-slate-300 bg-black/30 rounded-lg p-3 max-h-64 overflow-auto whitespace-pre-wrap break-words">
                  {JSON.stringify(v, null, 2)}
                </pre>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Collapsible JSON tree viewer with per-source tabs, search, dim-empty and
// click-to-copy field paths. Always capped in height. Dependency-light.
export function RawJson({ data, title = "Raw document", testid, sources = null, coerceNote = null }) {
  // `sources` (optional): [{ key, label, data, note }] → adds an Enriched/Raw source switcher.
  const srcList = React.useMemo(
    () => (Array.isArray(sources) && sources.length ? sources : [{ key: "_", label: null, data, note: coerceNote }]),
    [sources, data, coerceNote]
  );
  const [srcKey, setSrcKey] = React.useState(srcList[0]?.key);
  const [q, setQ] = React.useState("");
  const [dimEmpty, setDimEmpty] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [copiedPath, setCopiedPath] = React.useState(null);
  const [openPaths, setOpenPaths] = React.useState(() => topLevelOpen(srcList[0]?.data));
  const [expandedLeaves, setExpandedLeaves] = React.useState(() => new Set());

  const active = srcList.find((s) => s.key === srcKey) || srcList[0];
  const activeData = active?.data;
  const rootLabel = active?.label ? active.label.split(/\s+/)[0] : "";
  const needle = q.trim().toLowerCase();

  // Reset the tree to its default (top-level open) state when the source tab
  // actually changes — keyed on srcKey so re-renders that only change the data
  // object's identity don't wipe the user's expand/collapse state.
  const initRef = React.useRef(srcList[0]?.key);
  React.useEffect(() => {
    if (initRef.current !== srcKey) {
      initRef.current = srcKey;
      setOpenPaths(topLevelOpen(activeData));
      setExpandedLeaves(new Set());
    }
  }, [srcKey, activeData]);

  const rootEntries = React.useMemo(() => childEntries(activeData), [activeData]);

  const allBranchPaths = React.useMemo(() => {
    const s = new Set();
    const walk = (val, path) => {
      const ents = childEntries(val);
      if (ents.length === 0) return;
      if (path) s.add(path);
      for (const [seg, v, isArr] of ents) walk(v, childPath(path, seg, isArr));
    };
    walk(activeData, "");
    return s;
  }, [activeData]);

  // Search: collect which paths are visible, which to force-open, and which
  // keys/values directly matched (for highlight).
  const searchInfo = React.useMemo(() => {
    if (!needle) return null;
    const visible = new Set(), expand = new Set(), matchedKeys = new Set(), matchedVals = new Set();
    const walk = (val, path, keySeg) => {
      const ents = childEntries(val);
      const isBranch = ents.length > 0;
      const keyMatch = keySeg != null && String(keySeg).toLowerCase().includes(needle);
      if (!isBranch) {
        const valMatch = leafString(val).toLowerCase().includes(needle);
        if (keyMatch || valMatch) {
          visible.add(path);
          if (keyMatch) matchedKeys.add(path);
          if (valMatch) matchedVals.add(path);
        }
        return keyMatch || valMatch;
      }
      let anyChild = false;
      for (const [seg, v, isArr] of ents) {
        if (walk(v, childPath(path, seg, isArr), seg)) anyChild = true;
      }
      const vis = keyMatch || anyChild;
      if (vis && path) visible.add(path);
      if (keyMatch && path) matchedKeys.add(path);
      if (anyChild && path) expand.add(path);
      return vis;
    };
    for (const [seg, v, isArr] of rootEntries) walk(v, childPath("", seg, isArr), seg);
    return { visible, expand, matchedKeys, matchedVals };
  }, [needle, rootEntries]);

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(activeData, null, 2));
      setCopied(true); setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Clipboard copy failed:", err);
    }
  };

  const copyPath = async (path) => {
    const full = rootLabel ? (path.startsWith("[") ? rootLabel + path : `${rootLabel}.${path}`) : path;
    try { await navigator.clipboard.writeText(full); } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Clipboard copy failed:", err);
    }
    setCopiedPath(path);
    setTimeout(() => setCopiedPath((p) => (p === path ? null : p)), 900);
  };

  const toggle = (path) => setOpenPaths((prev) => {
    const n = new Set(prev);
    n.has(path) ? n.delete(path) : n.add(path);
    return n;
  });
  const expandAll = () => setOpenPaths(new Set(allBranchPaths));
  const collapseAll = () => setOpenPaths(new Set());

  const isOpen = (path) =>
    searchInfo ? (searchInfo.expand.has(path) || openPaths.has(path)) : openPaths.has(path);

  function renderNode(seg, value, path, depth) {
    if (searchInfo && !searchInfo.visible.has(path)) return null;
    const ents = childEntries(value);
    const isBranch = ents.length > 0;
    const dim = dimEmpty && allEmpty(value);
    const keyHit = !!searchInfo?.matchedKeys.has(path);
    const indent = { paddingLeft: depth * 14 };
    const keyCls = `cursor-pointer rounded px-0.5 ${dim ? "text-amber-300/45" : "text-teal-300"} hover:bg-teal-500/10 ${keyHit ? "bg-teal-500/25" : ""}`;
    const keyEl = (
      <>
        <span onClick={() => copyPath(path)} title="Copy path" data-testid={`${testid}-key-${path}`} className={keyCls}>{seg}</span>
        {copiedPath === path && <span className="text-[9px] text-emerald-300 font-mono ml-1">Copied</span>}
      </>
    );

    if (!isBranch) {
      const valHit = !!searchInfo?.matchedVals.has(path);
      return (
        <div key={path} style={indent} className="flex items-start gap-1 leading-relaxed">
          <span className="inline-block w-3 shrink-0" aria-hidden />
          {keyEl}
          <span className="text-slate-500">:</span>
          <LeafValue value={value} path={path} dim={dim} hit={valHit}
            expanded={expandedLeaves.has(path)}
            onShowMore={() => setExpandedLeaves((prev) => new Set(prev).add(path))} />
        </div>
      );
    }

    const open = isOpen(path);
    const count = Array.isArray(value) ? `${value.length} items` : `${Object.keys(value).length} keys`;
    const Chevron = open ? ChevronDown : ChevronRight;
    return (
      <div key={path} style={indent} className="leading-relaxed">
        <div className="flex items-start gap-1">
          <button type="button" onClick={() => toggle(path)} data-testid={`${testid}-toggle-${path}`}
            className="text-slate-500 hover:text-slate-200 shrink-0 mt-[1px]">
            <Chevron className="w-3 h-3" />
          </button>
          {keyEl}
          <span className="text-slate-500">{Array.isArray(value) ? "[" : "{"}</span>
          <span className={`text-[10px] ${dim ? "text-amber-300/40" : "text-slate-500"}`}>{count}</span>
          {!open && <span className="text-slate-500">{Array.isArray(value) ? "]" : "}"}</span>}
        </div>
        {open && ents.map(([cseg, cv, isArr]) => renderNode(cseg, cv, childPath(path, cseg, isArr), depth + 1))}
        {open && <div style={indent} className="text-slate-500 pl-4">{Array.isArray(value) ? "]" : "}"}</div>}
      </div>
    );
  }

  const noMatches = searchInfo && !rootEntries.some(([seg, , isArr]) => searchInfo.visible.has(childPath("", seg, isArr)));

  return (
    <section className="rounded-xl border border-white/[0.09] bg-[#111C34] overflow-hidden shadow-[0_12px_32px_rgba(0,0,0,0.45),0_2px_6px_rgba(0,0,0,0.28),inset_0_1px_0_rgba(255,255,255,0.06)]" data-testid={testid}>
      <header className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-white/[0.08] flex-wrap"
              style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(0,0,0,0.14) 100%)" }}>
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-[#C8A46B] font-mono">{title}</h3>
        <div className="flex items-center gap-2 flex-wrap">
          {srcList.length > 1 && srcList.map((s) => (
            <button key={s.key} onClick={() => setSrcKey(s.key)}
              data-testid={`${testid}-src-${s.key}`}
              className={`text-[10px] font-mono px-2 py-1 rounded border ${srcKey === s.key ? "border-teal-500/40 bg-teal-500/15 text-teal-200" : "border-white/10 text-slate-400 hover:bg-white/5"}`}>
              {s.label}
            </button>
          ))}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
            data-testid={`${testid}-search`}
            className="text-[11px] font-mono bg-black/30 border border-white/10 rounded px-2 py-1 text-slate-200 placeholder:text-slate-600 w-32 focus:outline-none focus:border-teal-500/40" />
          <button onClick={() => setDimEmpty((v) => !v)} data-testid={`${testid}-dim-empty`}
            className={`text-[10px] font-mono px-2 py-1 rounded border ${dimEmpty ? "border-amber-500/40 bg-amber-500/10 text-amber-200" : "border-white/10 text-slate-400 hover:bg-white/5"}`}>
            Dim empty {dimEmpty ? "✓" : ""}
          </button>
          <button onClick={expandAll} data-testid={`${testid}-expand-all`}
            className="text-[10px] font-mono px-2 py-1 rounded border border-white/10 text-slate-400 hover:bg-white/5">Expand all</button>
          <button onClick={collapseAll} data-testid={`${testid}-collapse-all`}
            className="text-[10px] font-mono px-2 py-1 rounded border border-white/10 text-slate-400 hover:bg-white/5">Collapse all</button>
          <button onClick={copyJson} data-testid={`${testid}-copy`} className="text-[11px] font-mono text-slate-400 hover:text-[#C8A46B]">
            {copied ? "Copied ✓" : "Copy JSON"}
          </button>
        </div>
      </header>
      {active?.note && (
        <div className="px-4 py-1.5 text-[10.5px] font-mono text-amber-300/80 border-b border-white/[0.06] bg-amber-500/[0.04]">{active.note}</div>
      )}
      <div className="p-4 text-[11px] font-mono text-slate-300 max-h-96 overflow-auto" data-testid={`${testid}-tree`}>
        {rootEntries.length === 0 && <div className="text-slate-600">Empty document.</div>}
        {noMatches && <div className="text-slate-600">No matches for “{q}”.</div>}
        {rootEntries.map(([seg, v, isArr]) => renderNode(seg, v, childPath("", seg, isArr), 0))}
      </div>
    </section>
  );
}

// ── RawJson tree helpers ─────────────────────────────────────────────────────
function childEntries(val) {
  // Entries of a branch as [segment, value, isArrayIndex]; [] for leaves
  // (primitives AND empty objects/arrays, which render as {} / []).
  if (!(val && typeof val === "object")) return [];
  if (Array.isArray(val)) return val.map((v, i) => [`[${i}]`, v, true]);
  return Object.entries(val).map(([k, v]) => [k, v, false]);
}
function childPath(parent, seg, isArr) {
  if (!parent) return seg;
  return isArr ? `${parent}${seg}` : `${parent}.${seg}`;
}
function topLevelOpen(obj) {
  const s = new Set();
  for (const [seg, v, isArr] of childEntries(obj)) {
    if (childEntries(v).length > 0) s.add(childPath("", seg, isArr));
  }
  return s;
}
function isEmptyValue(v) {
  if (v === "" || v === null || v === 0) return true;
  if (Array.isArray(v)) return v.length === 0;
  if (v && typeof v === "object") return Object.keys(v).length === 0;
  return false;
}
function allEmpty(v) {
  if (!(v && typeof v === "object")) return isEmptyValue(v);
  const vals = Array.isArray(v) ? v : Object.values(v);
  if (vals.length === 0) return true;
  return vals.every(allEmpty);
}
function leafString(v) {
  if (v === null) return "null";
  if (v && typeof v === "object") return Array.isArray(v) ? "[]" : "{}";
  return String(v);
}
function LeafValue({ value, path, dim, hit, expanded, onShowMore }) {
  const base = dim ? "text-amber-300/45" :
    value === null ? "text-slate-500" :
    typeof value === "string" ? "text-emerald-300" :
    typeof value === "number" ? "text-sky-300" :
    typeof value === "boolean" ? "text-violet-300" : "text-slate-400";
  const hl = hit ? "bg-teal-500/25 rounded px-0.5" : "";
  if (typeof value === "string" && value.length > 160 && !expanded) {
    return (
      <span className={`${base} ${hl} break-all`} data-testid={`leaf-${path}`}>
        {value.slice(0, 160)}…{" "}
        <button type="button" onClick={onShowMore}
          className="text-[10px] text-teal-400 hover:text-teal-200 underline decoration-dotted">
          show more ({value.length} chars)
        </button>
      </span>
    );
  }
  return <span className={`${base} ${hl} break-all`} data-testid={`leaf-${path}`}>{leafString(value)}</span>;
}
